import { transformEnumToDisplay } from '@fruitful/fruitils';
import { EmploymentStatus } from '@fruitful/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Redirect, router, useLocalSearchParams } from 'expo-router';
import { useCallback, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Form,
    ScreenContainer,
    SelectSingle,
    Spinner,
    USE_FORM_MODE_DEFAULTS,
    useToastController,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import LogRocketFacade from '../../logger/log-rocket/LogRocketFacade';
import { captureException } from '../../logger/Logger';
import useUpdateUser from '../../user/hooks/useUpdateUser';
import { useUser } from '../../user/providers/UserProvider';
import { useTrackFinancialProfileCompletion } from '../hooks/useTrackFinancialProfileCompletion';
import { getMemberProfileUserSubtitle } from '../utils/memberProfileUtils';
import {
    SUCCESS_TOAST_DURATION,
    SUCCESS_TOAST_MESSAGE,
    SuccessToastIcon,
} from './SuccessToast';

const employmentStatusSchema = z.object({
    employmentStatus: z.nativeEnum(EmploymentStatus, {
        required_error: 'Please select an employment status',
    }),
});

type EmploymentStatusSchema = z.infer<typeof employmentStatusSchema>;

export const Employment = () => {
    LogRocketFacade.tagPage('Profile/EmploymentStatus');

    const toast = useToastController();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const authedUser = useAuthedUser();
    const { userId } = useLocalSearchParams<{ userId?: string }>();

    // Use provided userId or fallback to authenticated user. Find the target user.
    const targetUserId = userId ?? authedUser.id;
    const targetUser = [authedUser, authedUser.partnerMember].find(
        (user) => user?.id === targetUserId,
    );

    const { refetch: refetchUser } = useUser();
    const { mutateAsync: updateUser, isPending: isUpdatingUser } =
        useUpdateUser();

    const { trackFinancialProfileUpdate } =
        useTrackFinancialProfileCompletion();

    const {
        handleSubmit,
        formState: { isValid },
        setValue,
        watch,
    } = useForm<EmploymentStatusSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(employmentStatusSchema),
        defaultValues: {
            employmentStatus: targetUser?.employmentStatus ?? undefined,
        },
    });

    const watchedEmploymentStatus = watch('employmentStatus');

    const employmentStatusOptions = Object.values(EmploymentStatus).map(
        (status) => ({
            title: transformEnumToDisplay(status),
        }),
    );

    const onSubmit: SubmitHandler<EmploymentStatusSchema> = useCallback(
        async (data) => {
            if (targetUser == null) {
                captureException(
                    new Error(
                        'Target user not found for employment status update',
                    ),
                );
                return;
            }

            setIsSubmitting(true);

            try {
                await updateUser({
                    urlParams: { userId: targetUserId },
                    body: {
                        employmentStatus: data.employmentStatus,
                    },
                });

                void refetchUser?.();

                toast.show(SUCCESS_TOAST_MESSAGE, {
                    duration: SUCCESS_TOAST_DURATION,
                    icon: SuccessToastIcon,
                });

                trackFinancialProfileUpdate({
                    employmentUpdated: data.employmentStatus != null,
                });

                router.back();
            } catch (error) {
                captureException(error);
                toast.show('Failed to update employment status', {
                    duration: 2000,
                });
            } finally {
                setIsSubmitting(false);
            }
        },
        [
            targetUser,
            updateUser,
            refetchUser,
            toast,
            trackFinancialProfileUpdate,
        ],
    );

    const handleEmploymentStatusChange = (index: number) => {
        const selectedStatus = Object.values(EmploymentStatus)[index];
        if (selectedStatus != null) {
            setValue('employmentStatus', selectedStatus, {
                shouldValidate: true,
            });
        }
    };

    const isFormSubmitting = isSubmitting || isUpdatingUser;

    // Should not happen, but added as a defensive check.
    if (targetUser == null) {
        captureException(
            new Error('Target user not found for employment status page'),
        );
        return <Redirect href="/settings" />;
    }

    return (
        <ScreenContainer
            title="Employment status"
            subtitle={getMemberProfileUserSubtitle(authedUser, targetUser)}
            hasBackButton={true}
        >
            {isFormSubmitting ? (
                <Spinner />
            ) : (
                <YStack gap={24} justifyContent="space-between">
                    <YStack gap={16}>
                        <YStack gap={8}>
                            <BodyMediumRegular>
                                What is your current employment status?
                            </BodyMediumRegular>
                        </YStack>

                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <SelectSingle
                                options={employmentStatusOptions}
                                selectedIndex={Object.values(
                                    EmploymentStatus,
                                ).findIndex(
                                    (status) =>
                                        status === watchedEmploymentStatus,
                                )}
                                onSelect={handleEmploymentStatusChange}
                            />
                        </Form>
                    </YStack>

                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Save"
                        onPress={handleSubmit(onSubmit)}
                        disabled={!isValid || isFormSubmitting}
                    />
                </YStack>
            )}
        </ScreenContainer>
    );
};
